import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { translations } from "@/i18n/translations";
import {
  getCanonicalUrl,
  getLocalizedPathsForRoute,
  getRobotsContent,
  trimTrailingSlash,
} from "@/data/localizedPaths";
import { readPageMeta } from "@/lib/pageMetaStore";
import ogImage from "@/assets/intygslakaren-og-image.png";

const BASE_URL = "https://intygslakaren.se";

/**
 * Canonical, hreflang, robots, and social meta. Relies on PageMeta having
 * published title/description earlier in the same render (via Routes).
 * Render this component AFTER <Routes> so page meta is available.
 */
export const SeoHeadLinks = () => {
  const { pathname } = useLocation();
  const { lang } = useLang();
  const robots = getRobotsContent(pathname);
  const localized = getLocalizedPathsForRoute(pathname);
  const { title: pageTitle, description: pageDescription } = readPageMeta();

  const home = translations[lang].hero;
  const isHome = trimTrailingSlash(pathname) === "/" || trimTrailingSlash(pathname) === "/en";
  const title = isHome ? home.metaTitle.slice(0, 60) : pageTitle || home.metaTitle;
  const description = isHome
    ? home.metaDescription.slice(0, 160)
    : pageDescription || home.metaDescription;

  const url = getCanonicalUrl(pathname, lang, BASE_URL);
  const imageUrl = ogImage.startsWith("http") ? ogImage : `${BASE_URL}${ogImage}`;
  const locale = lang === "sv" ? "sv_SE" : "en_GB";
  const alternateLocale = lang === "sv" ? "en_GB" : "sv_SE";
  const canonicalHref = localized
    ? `${BASE_URL}${lang === "sv" ? localized.sv : localized.en}`
    : url;

  // Direct children (array), not a Fragment — react-helmet-async skips Fragment kids.
  const alternateLinks = localized
    ? [
        <link
          key="hreflang-sv"
          rel="alternate"
          href={`${BASE_URL}${localized.sv}`}
          hrefLang="sv"
        />,
        <link
          key="hreflang-en"
          rel="alternate"
          href={`${BASE_URL}${localized.en}`}
          hrefLang="en"
        />,
        <link
          key="hreflang-x-default"
          rel="alternate"
          href={`${BASE_URL}${localized.sv}`}
          hrefLang="x-default"
        />,
      ]
    : [];

  return (
    <Helmet>
      <html lang={lang} />
      <meta name="robots" content={robots} />
      <meta name="googlebot" content={robots} />
      <link rel="canonical" href={canonicalHref} />
      {alternateLinks}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Intygsläkaren" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:locale" content={locale} />
      <meta property="og:locale:alternate" content={alternateLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
    </Helmet>
  );
};
